import moment from 'moment-timezone';
import config from '../constants/config';
import { getToken } from './session';
import {
    NOTIFICATION_SUBSCRIBE, NOTIFICATION_UNSUBSCRIBE, COMMAND_INSERT, TOPIC_LATEST_EG_SENSOR
} from '../constants';

class WebSocketClient {
    constructor() {
        this.socket = null;
        this.isConnected = false;
        this.interval = null;
        this.requestList = {};
        this.requestId = 0;
        this.msgCallbackList = {};
        this.actionList = {};
        this.isAuthenticating = false;
        this.isAuthenticated = false;
        this.isPrintLog = false;

        this.connect = this.connect.bind(this);
        this.authenticate = this.authenticate.bind(this);
        this.messageHandler = this.messageHandler.bind(this);
        this.getRequestId = this.getRequestId.bind(this);
        this.subscribe = this.subscribe.bind(this);
        this.subscribeAll = this.subscribeAll.bind(this);
        this.unsubscribe = this.unsubscribe.bind(this);
        this.addMessageReceiver = this.addMessageReceiver.bind(this);
        this.removeMessageReceiver = this.removeMessageReceiver.bind(this);
        this.destroy = this.destroy.bind(this);
        this.parseJSON = this.parseJSON.bind(this);
        this.infoLog = this.infoLog.bind(this);
            }

    connect(callback) {
        if (!config.websocket.IS_ENABLE) return callback('Websocket connectivity is disabled!');

        const me = this;
        this.destroy();
        const websocketUrl = config.websocket.SOCKET_URL
            ? config.websocket.SOCKET_URL : "ws://staging01.acuitusag.network:32002";
        this.socket = new WebSocket(`${websocketUrl}/api/websocket`);

        this.socket.onmessage = (event) => {
            const message = event.data ? me.parseJSON(event.data) : null;
            if (!message) return;
            this.infoLog(message, "onMessage");

            if (message.hasOwnProperty('request_id') && me.requestList.hasOwnProperty(message.request_id)) {
                const { cb } = me.requestList[message.request_id];
                cb(message);
                delete me.requestList[message.request_id];
            }

            if (message.error) {
                if (message.error === 'Unauthorized') me.authenticate();
            } else if (message.success) me.updateSubscribeId(message);
            else if (message.hasOwnProperty("request_id")) me.messageHandler(message);
            else if (message.hasOwnProperty("subscription_id")) me.messageHandler(message);
        };

        this.socket.onopen = () => {
            this.infoLog('Websocket:=> connected');
            me.isConnected = true;
            me.authenticate();
            return callback();
        };

        this.socket.onerror = (err) => {
            this.infoLog('Websocket:=> Error : ' + err);
        };

        this.socket.onclose = () => {
            this.infoLog('Websocket:=> Disconnected');

            me.isConnected = false;
            me.interval = window.setInterval(() => {
                if (me.isConnected) {
                    clearInterval(me.interval);
                    me.interval = null;
                    return;
                }

                this.infoLog('Websocket:=> Reconnecting...');
                me.connect(callback);
            }, 1500000);
        };
    }

    sendCommand(data) {
        const params = {
            "action": COMMAND_INSERT,
            "deviceId": data.deviceId,
            "command": data.command
        }
        if (this.isConnected && (this.socket && this.socket.readyState === this.socket.OPEN)) this.socket.send(JSON.stringify(params));
    }

    authenticate() {
        if (!(this.socket && (this.socket.readyState === this.socket.OPEN)) || this.isAuthenticating) return;

        this.isAuthenticating = true;
        this.isAuthenticated = false;
        this.infoLog('Websocket:=> Authenticating...');

        const params = {
            action: "authenticate",
            request_id: this.getRequestId(() => {
                this.infoLog('Websocket:=> Authenticated!');
                this.isAuthenticated = true;
                this.subscribeAll();
            }),
            token: getToken()
        }

        this.socket.send(JSON.stringify(params));
    }

    subscribe(notifications, group, unsubscribeExists = true) {
        if (!group) return;
        if (!Array.isArray(notifications) && typeof notifications === 'object') notifications = [notifications];
        const defaultItem = { requestId: null, subscriptionId: null, action: NOTIFICATION_SUBSCRIBE };

        const actionList = [];
        if (Array.isArray(notifications)) {
            notifications.forEach(element => {
                if (element.hasOwnProperty('deviceId'))
                    actionList.push({
                        ...defaultItem,
                        deviceId: element.deviceId,
                        subscribeId: element.subscribeId,
                        ...element.name ? { name: element.name } : {},
                        ...element.action ? { action: element.action } : {},
                        ...element.parameters ? { parameters: element.parameters } : {},
                        ...element.filters ? { filters: element.filters } : {}
                    });
            }, this);
        }

        if (this.socket && unsubscribeExists && this.actionList.hasOwnProperty(group)) {
            const unsubscribeIds = actionList.filter(action => action.subscribeId).map(action => action.subscribeId);
            this.unsubscribe(group, unsubscribeIds.length === 0, unsubscribeIds);
        }

        if (this.socket && this.socket.readyState === this.socket.OPEN && this.isAuthenticated) {
            actionList.forEach(e => {

                const idType = {
                    "latest_eg_sensor": "equipment_group_uuid",
                    "sensor": "device_uuid",
                    "q-lost": "device_uuid",
                    "latest_farm_kpi": "farm_uuid",
                    "farm_kpi": "farm_uuid",
                    "farm_load_kpi": "farm_uuid",
                    "latest_crew_kpi": "crew_uuid",
                    "crew_kpi": "crew_uuid",
                    "crew_load_kpi": "crew_uuid",
                    "latest_eg_kpi": "equipment_group_uuid",
                    "equipment_group_kpi": "equipment_group_uuid"
                };

                const idName = e.name ? idType[e.name] : idType[e.action];

                const params = {
                    action: e.action,
                    request_id: this.getRequestId(),
                    [idName]: e.deviceId,
                    ...e.name && { subscription_type: e.name },
                    ...e.parameters,
                    ...e.filters
                };

                // console.log(params)

                e.requestId = params.request_id;
                this.socket.send(JSON.stringify(params));
            }, this);
        }

        const existsActionList = this.actionList.hasOwnProperty(group) ? this.actionList[group] : [];
        this.actionList[group] = [...existsActionList, ...actionList];
    }

    subscribeAll() {
        if (this.socket && this.socket.readyState === this.socket.OPEN && this.isAuthenticated) {
            Object.keys(this.actionList).forEach(group => {
                this.actionList[group].forEach(e => {
                    const params = {
                        action: e.action,
                        request_id: this.getRequestId(),
                        deviceId: e.deviceId,
                        names: [e.name],
                        subscription_type: e.name,
                        ...e.parameters
                    }
                    e.requestId = params.request_id;
                    e.subscriptionId = null;
                    this.socket.send(JSON.stringify(params));
                });
            }, this);
        }
    }

    unsubscribe(group, isDelete = true, unsubscribeIds = []) {
        const unsubscribeAll = unsubscribeIds.length === 0;
        if (typeof group === 'string') {
            if (!this.actionList.hasOwnProperty(group)) return;

            if (this.socket && this.socket.readyState === this.socket.OPEN && this.isAuthenticated) {
                this.actionList[group].filter(e => e.action === NOTIFICATION_SUBSCRIBE)
                    .forEach(element => {
                        if (!element.subscriptionId) return;
                        if (unsubscribeAll || (element.subscribeId && unsubscribeIds.indexOf(element.subscribeId) >= 0)) {
                            const params = {
                                action: NOTIFICATION_UNSUBSCRIBE,
                                subscription_id: element.subscriptionId
                            }
                            this.socket.send(JSON.stringify(params));
                        }
                    }, this);
                if (!unsubscribeAll)
                    this.actionList[group] = this.actionList[group].filter(action =>
                        !(action.subscribeId && unsubscribeIds.indexOf(action.subscribeId) >= 0));
                if (isDelete) delete this.actionList[group];
            }
        }
    }

    addMessageReceiver(eventName, names, cb) {
        this.msgCallbackList[eventName] = { names, cb };
    }

    removeMessageReceiver(eventName) {
        if (this.msgCallbackList.hasOwnProperty(eventName)) delete this.msgCallbackList[eventName];
    }

    onMessage(cb) {
        this.msgCallbackList['default'] = { names: null, cb };
    }

    updateSubscribeId(data) {
        let isMatched = false;
        const isAutoRefresh = localStorage.getItem("mapAutoRefresh");

        if (isAutoRefresh && data.subscription_id) {
            Object.keys(this.actionList).forEach(group => {
                if (isMatched) return;
                this.actionList[group].forEach(e => {
                    if (e.requestId === data.request_id) {
                        e.subscriptionId = data.subscription_id;
                        isMatched = true;
                        return;
                    }
                });
            }, this);
        }
    }

    getRequestId(callback = null) {
        this.requestId++;
        if (callback) this.requestList[this.requestId] = { cb: callback };
        return this.requestId;
    }

    messageHandler(message) {
        if (Object.keys(this.msgCallbackList).length === 0) return;
        const isAutoRefresh = JSON.parse(localStorage.getItem("mapAutoRefresh"));
        let updatedMessage;
        if (!isAutoRefresh) {
            updatedMessage = {};
        } else {
            updatedMessage = message;
        }
        const { actionInfo, groupName } = this.getActionInfo(updatedMessage.request_id, updatedMessage.subscription_id);
        const deviceId = actionInfo && actionInfo.deviceId;
        const messageId = actionInfo && actionInfo.action === TOPIC_LATEST_EG_SENSOR
            ? (actionInfo.parameters && actionInfo.parameters.equipment_group_uuid) : deviceId;

        const messageData = updatedMessage.request_id && actionInfo
            && messageId && updatedMessage.data && updatedMessage.data.data && updatedMessage.data.data[messageId]
            ? updatedMessage.data.data[messageId] : updatedMessage.data;

        const receivedData = {
            deviceId,
            action: actionInfo && actionInfo.action,
            groupName: groupName,
            name: actionInfo ? (actionInfo.name ? actionInfo.name : actionInfo.action) : "",
            message: messageData,
            initial: updatedMessage.request_id ? true : false,
            ts: updatedMessage && updatedMessage.data && updatedMessage.data.ts ? updatedMessage.data.ts : moment.utc().valueOf()
        };

        Object.keys(this.msgCallbackList).forEach(key => {
            if (this.msgCallbackList[key].names === null) this.msgCallbackList[key].cb(receivedData);
            else if (this.msgCallbackList[key].names.indexOf(receivedData.name) >= 0) this.msgCallbackList[key].cb(receivedData);
        }, this);
    }

    getActionInfo(requestId, subscriptionId) {
        let actionInfo = null;
        let groupName = null;
        Object.keys(this.actionList).forEach(group => {
            if (!actionInfo) {
                const find = this.actionList[group].filter(e => requestId ? e.requestId === requestId : e.subscriptionId === subscriptionId);
                actionInfo = find.length > 0 ? find[0] : null;
                groupName = group;
            }
        }, this);

        return { actionInfo, groupName };
    }

    getActionList() {
        return this.actionList;
    }

    destroy() {
        if (this.socket) {
            this.socket.close();
            delete this.socket;
            this.socket = null;
        }
    }

    parseJSON(message) {
        try {
            return JSON.parse(message);
        } catch (error) {
            return null;
        }
    }

    infoLog(msg, type) {
        if (this.isPrintLog) console.log(msg, type);
    }
}

export default WebSocketClient;